<template>
  <transition name="fade">
    <div v-if="playerStore.jackpotNotification.show" class="jackpot-overlay">
      <div class="jackpot-notification-content">
        <!-- Etiqueta de Jackpot -->
        <div class="jackpot-label">Jackpot</div>
        
        <!-- Mensaje principal del jackpot -->
        <div class="jackpot-message">
          <span>¡Jackpot ganado:</span>
          <div class="amount">{{ formattedAmount }}</div>
          <div class="jackpot-name">{{ jackpotName }}</div>
        </div>
        
        <!-- Botón de cerrar -->
        <button @click="clearNotification" class="close-button">
          ACEPTAR
        </button>
      </div>
    </div>
  </transition>
</template>

<script>
import { computed, ref, onMounted, watch } from 'vue'
import { usePlayerStore } from '../stores/player/playerStore'

export default {
  name: 'JackpotNotification',
  setup() {
    const playerStore = usePlayerStore()
    const isVisible = ref(true)
    const timeoutId = ref(null)

    const jackpotName = computed(() => playerStore.jackpotNotification.jackpotName || '')
    
    const formattedAmount = computed(() => {
      return Number(playerStore.jackpotNotification.amount).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    })

    const clearNotification = () => {
      isVisible.value = false
      setTimeout(() => {
        playerStore.clearJackpotNotification()
      }, 500)
    }

    const setupAutoHide = () => {
      if (timeoutId.value) {
        clearTimeout(timeoutId.value)
      }
      
      timeoutId.value = setTimeout(() => {
        isVisible.value = false
        setTimeout(() => {
          playerStore.clearJackpotNotification()
        }, 500)
      }, 120000) // 2 minutos
    }

    onMounted(() => {
      if (playerStore.jackpotNotification && playerStore.jackpotNotification.show) {
        setupAutoHide()
      }
    })

    watch(() => playerStore.jackpotNotification.show, (show) => {
      if (show) {
        isVisible.value = true
        setupAutoHide()
      }
    })

    return {
      jackpotName,
      formattedAmount,
      clearNotification,
      playerStore
    }
  }
}
</script>

<style scoped>
.jackpot-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: none; /* Permite hacer clic a través del overlay */
}

.jackpot-notification-content {
  position: relative;
  pointer-events: auto; /* Restaura los eventos para este elemento */
  width: 400px; /* Recuadro más grande */
  max-width: 90%;
  text-align: center;
  padding: 25px 30px; /* Más padding para hacerlo más grande */
  background-color: #7e0101; /* Fondo rojo oscuro para diferenciarlo del cashback */
  border-radius: 12px;
  box-shadow: 0 0 30px rgba(255, 215, 0, 0.8); /* Brillo dorado más intenso */
  border: 6px solid; /* Borde más visible */
  border-color: #ffff00; /* Color base del borde */
  animation: jackpot-glow 2s infinite alternate; /* Animación del borde más rápida */
}

.jackpot-label {
  position: absolute;
  top: -15px;
  left: 20px;
  background: linear-gradient(to bottom, #ff0000, #cc0000);
  color: white;
  font-size: 16px;
  font-weight: bold;
  padding: 3px 12px;
  border-radius: 4px;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.5);
}

.jackpot-message {
  color: white;
  font-size: 24px; /* Texto principal más grande */
  font-weight: bold;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.7);
  margin: 15px 0 25px 0; /* Más espacio abajo */
}

.amount {
  font-size: 46px; /* Monto más grande que el cashback */
  margin: 15px 0 10px;
  color: #ffcc00; /* Dorado para el jackpot */
  text-shadow: 
    0 0 5px #ff0000, /* Brillo rojo */
    0 0 10px #ff6600, /* Brillo naranja */
    0 0 15px #ffff00; /* Brillo amarillo */
  animation: jackpot-pulse 1s infinite alternate;
}

.jackpot-name {
  font-size: 20px;
  color: #ffff00; /* Amarillo para el nombre del jackpot */
  margin-top: 5px;
  text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.8);
}

.close-button {
  margin-top: 20px;
  background: linear-gradient(to bottom, #ffcc00, #ff9900);
  color: #000000;
  border: none;
  padding: 12px 30px;
  border-radius: 25px;
  font-size: 18px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
  text-transform: uppercase;
  letter-spacing: 1px;
}

.close-button:hover {
  transform: scale(1.05);
  background: linear-gradient(to bottom, #ffdd00, #ffaa00);
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.4);
}

@keyframes jackpot-pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.08); /* Mayor escala que el cashback */
    opacity: 0.9;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes jackpot-glow {
  0% {
    box-shadow: 0 0 15px #ffff00, 0 0 30px rgba(255, 0, 0, 0.5);
  }
  100% {
    box-shadow: 0 0 25px #ffff00, 0 0 50px rgba(255, 0, 0, 0.8);
  }
}

/* Animaciones de entrada/salida */
.fade-enter-active {
  animation: jackpot-in 0.5s ease-out;
}

.fade-leave-active {
  animation: jackpot-out 0.5s ease-in;
}

@keyframes jackpot-in {
  0% {
    opacity: 0;
    transform: scale(0.5); /* Empieza más pequeño que el cashback */
  }
  70% {
    opacity: 1;
    transform: scale(1.15); /* Rebasa un poco más el tamaño final */
  }
  100% {
    transform: scale(1);
  }
}

@keyframes jackpot-out {
  from {
    opacity: 1;
    transform: scale(1);
  }
  to {
    opacity: 0;
    transform: scale(0.8);
  }
}

/* Responsive para dispositivos móviles */
@media screen and (max-width: 480px) {
  .jackpot-notification-content {
    padding: 20px 15px;
  }
  
  .jackpot-message {
    font-size: 20px;
  }
  
  .amount {
    font-size: 38px;
  }
  
  .jackpot-name {
    font-size: 18px;
  }
  
  .close-button {
    padding: 10px 25px;
    font-size: 16px;
  }
}
</style>